import { useCallback, useRef, useState } from 'react';

import isEmpty from 'lodash-es/isEmpty';
import { Trash2Icon, XIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { AlertWithReferences } from '@/components/atoms/AlertWithReferences/AlertWithReferences';
import { MODAL_WARN } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { pushModal } from '@/redux/modals/actions';
import { deleteValue, updateValue, createValue } from '@/redux/values/actions';
import { selectById } from '@/redux/values/selectors';
import { OptionBase } from '@/shared/types/common';

import './Value.scss';

const buildItem = (value: string, type: 'synonym' | 'pattern', data: string[]) => {
  if (!value) {
    return;
  }
  let examples = data.map((e) => e.trim()).filter((e) => e);
  examples = [...new Set(examples)].sort();

  if (!value.trim() || examples.length === 0) {
    return null;
  }

  const item = { type, value };
  if (type === 'synonym') {
    return { ...item, synonyms: examples };
  }
  return { ...item, patterns: examples };
};

interface ChipsInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
  disabled?: boolean;
  error?: boolean;
  className?: string;
}

const ChipsInput = ({
  value,
  onChange,
  placeholder,
  disabled,
  error,
  className,
}: ChipsInputProps) => {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');

  const addChip = () => {
    const next = draft.trim();
    if (!next) {
      return;
    }
    if (value.includes(next)) {
      setDraft('');
      return;
    }
    onChange([...value, next]);
    setDraft('');
  };

  /** Double-click loads a chip back into the input so it can be corrected. */
  const editChip = (chip: string) => {
    if (disabled) {
      return;
    }
    onChange(value.filter((c) => c !== chip));
    setDraft(chip);
  };

  return (
    <div
      className={cn(
        'flex min-h-9 flex-wrap items-center gap-1 rounded-md border bg-background px-2 py-1',
        'focus-within:border-ring',
        error ? 'border-destructive' : 'border-border',
        disabled && 'opacity-50',
        className
      )}
    >
      {value.map((chip) => (
        <span
          key={chip}
          onDoubleClick={() => editChip(chip)}
          className="inline-flex items-center gap-1 rounded-full bg-muted/50 px-2 py-0.5 text-xs"
        >
          {chip}
          {!disabled && (
            <button
              type="button"
              onClick={() => onChange(value.filter((c) => c !== chip))}
              className="text-muted-foreground hover:text-foreground"
              aria-label={t('common.remove_value', { 0: chip })}
            >
              <XIcon size={12} />
            </button>
          )}
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          // Enter only — a comma is a literal character in synonyms and in `{n,m}` patterns.
          if (e.key === 'Enter') {
            e.preventDefault();
            addChip();
          } else if (e.key === 'Backspace' && !draft && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        // Discard an uncommitted draft rather than saving it, matching the previous input.
        onBlur={() => setDraft('')}
        disabled={disabled}
        placeholder={value.length === 0 ? placeholder : ''}
        // The placeholder is dropped once a chip exists, so name the input explicitly.
        // aria-invalid too: the wrapper's border is otherwise the only error cue.
        aria-label={placeholder}
        aria-invalid={error || undefined}
        className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
      />
    </div>
  );
};

interface Props {
  id: string;
  readOnly?: boolean;
  findConnectedDialogs?: (value: string) => OptionBase<{ dialogId: string }>[];
  handleDialogNameClick?: (dialogName: string) => void;
}

export function ValuePure({
  id,
  findConnectedDialogs,
  handleDialogNameClick,
  readOnly = false,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const entity = useSelector((state) => selectById(state, id));
  const { value, type, synonyms, patterns } = entity ?? {};

  const [inputValue, setInputValue] = useState(value || '');
  const [inputType, setInputType] = useState(type || 'synonym');
  const [inputData, setInputData] = useState<string[]>(
    type === 'synonym' ? synonyms || [] : patterns || []
  );
  const inputRef = useRef<HTMLInputElement>(null);

  const onBlur = useCallback(() => {
    const newValue = buildItem(inputValue, inputType, inputData);
    if (!id && inputData.length) {
      if (!inputValue) {
        inputRef.current?.focus();
        return;
      }
      dispatch(createValue({ newValue }));
      return;
    }
    dispatch(updateValue({ id, newValue }));
  }, [dispatch, id, inputData, inputType, inputValue]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  }, []);

  const handleTypeChange = useCallback((v: string) => {
    setInputType(v as 'synonym' | 'pattern');
  }, []);

  const handleDataChange = useCallback(
    (newArray: string[]) => {
      const lastValue = newArray[newArray.length - 1];
      if (inputType === 'pattern' && lastValue) {
        try {
          new RegExp(lastValue);
        } catch (error) {
          console.error('Invalid regular expression:', (error as Error).message);
          return;
        }
      }
      const newInputData = [...new Set(newArray)];
      const newValue = buildItem(inputValue, inputType, newInputData);
      if (id) {
        dispatch(updateValue({ id, newValue }));
      } else {
        dispatch(createValue({ newValue }));
      }
      setInputData(newInputData);
    },
    [inputType, inputValue, id, dispatch]
  );

  const onDeleteHandle = useCallback(() => {
    const usedDialogs = findConnectedDialogs(value);
    if (!isEmpty(usedDialogs)) {
      const warning = (
        <AlertWithReferences
          references={usedDialogs}
          onRefClick={handleDialogNameClick}
          referenceProp="url"
        >
          <Trans i18nKey="entity.ref_warning" values={[value]} />
        </AlertWithReferences>
      );
      const warnProps = {
        title: t('common.warning'),
        children: warning,
        primaryButtonText: t('common.close'),
      };

      dispatch(pushModal(MODAL_WARN, warnProps));
    } else {
      dispatch(deleteValue({ id }));
    }
  }, [dispatch, findConnectedDialogs, handleDialogNameClick, id, t, value]);

  const showError = !!id && inputData.length === 0;

  return (
    <li className="entity__input">
      <input
        className="entity__input__value w-full bg-transparent text-sm text-ellipsis outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={onBlur}
        placeholder={t('entity.type_value')}
        aria-label="naked"
        disabled={readOnly}
        ref={inputRef}
      />
      <Select
        value={inputType}
        onValueChange={handleTypeChange}
        onOpenChange={(open) => {
          if (!open) {
            onBlur();
          }
        }}
        disabled={readOnly}
      >
        <SelectTrigger size="sm" className="entity__input__type">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="synonym">{t('entity.synonym')}</SelectItem>
          <SelectItem value="pattern">{t('entity.pattern')}</SelectItem>
        </SelectContent>
      </Select>
      <ChipsInput
        value={inputData}
        onChange={handleDataChange}
        placeholder={t('entity.type', {
          0: inputType === 'synonym' ? t('entity.synonym') : t('entity.pattern'),
        })}
        disabled={readOnly}
        error={showError}
        className="entity__input__data"
      />
      {!readOnly && id && (
        <Button
          className="hover:text-destructive hover:[&_svg]:text-destructive"
          variant="ghost-neutral"
          size="icon-sm"
          onClick={onDeleteHandle}
          aria-label={t('common.delete')}
        >
          <Trash2Icon size={16} />
        </Button>
      )}
    </li>
  );
}

export default ValuePure;
